// src/vec.ts
function add(A, B) {
  return [A[0] + B[0], A[1] + B[1]];
}
function sub(A, B) {
  return [A[0] - B[0], A[1] - B[1]];
}
function vec(A, B) {
  return [B[0] - A[0], B[1] - A[1]];
}
function mul(A, n) {
  return [A[0] * n, A[1] * n];
}
function div(A, n) {
  return [A[0] / n, A[1] / n];
}
function per(A) {
  return [A[1], -A[0]];
}
function dpr(A, B) {
  return A[0] * B[0] + A[1] * B[1];
}
function len(A) {
  return Math.hypot(A[0], A[1]);
}
function len2(A) {
  return A[0] * A[0] + A[1] * A[1];
}
function dist2(A, B) {
  return len2(sub(A, B));
}
function uni(A) {
  return div(A, len(A));
}
function dist(A, B) {
  return Math.hypot(A[1] - B[1], A[0] - B[0]);
}
function med(A, B) {
  return mul(add(A, B), 0.5);
}
function rotAround(A, C, r) {
  const s = Math.sin(r);
  const c = Math.cos(r);
  const px = A[0] - C[0];
  const py = A[1] - C[1];
  const nx = px * c - py * s;
  const ny = px * s + py * c;
  return [nx + C[0], ny + C[1]];
}
function lrp(A, B, t) {
  return add(A, mul(vec(A, B), t));
}
function isEqual(a, b) {
  return a[0] === b[0] && a[1] === b[1];
}

// src/utils.ts
function lerp(y1, y2, mu) {
  return y1 * (1 - mu) + y2 * mu;
}
function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}
function toPointsArray(points) {
  if (Array.isArray(points[0])) {
    return points.map(([x, y, pressure = 0.5]) => [
      x,
      y,
      pressure
    ]);
  } else {
    return points.map(({ x, y, pressure = 0.5 }) => [x, y, pressure]);
  }
}
function getStrokeRadius(size, thinning, easing, pressure = 0.5) {
  if (!thinning)
    return size / 2;
  pressure = clamp(easing(pressure), 0, 1);
  return (thinning < 0 ? lerp(size, size + size * clamp(thinning, -0.95, -0.05), pressure) : lerp(size - size * clamp(thinning, 0.05, 0.95), size, pressure)) / 2;
}

// src/index.ts
var { min, PI } = Math;
function getStrokePoints(points, options = {}) {
  let { streamline = 0.5 } = options;
  const { simulatePressure = true, last: isComplete = false } = options;
  if (points.length === 0)
    return [];
  streamline = streamline / (simulatePressure ? 3 : 2);
  const pts = toPointsArray(points);
  if (pts.length === 1)
    pts.push([...add(pts[0], [1, 1]), pts[0][2]]);
  const strokePoints = [];
  let prev = {
    point: [pts[0][0], pts[0][1]],
    pressure: pts[0][2],
    vector: [0, 0],
    distance: 0,
    runningLength: 0
  };
  strokePoints.push(prev);
  const len3 = pts.length;
  let curr;
  for (let i = 0; i < len3; i++) {
    curr = pts[i];
    const point = isComplete && i === len3 - 1 ? curr : lrp(prev.point, curr, 1 - streamline);
    if (isEqual(prev.point, point))
      continue;
    const vector = uni(sub(prev.point, point));
    const distance = dist(point, prev.point);
    const runningLength = prev.runningLength + distance;
    prev = {
      point,
      pressure: curr[2],
      vector,
      distance,
      runningLength
    };
    strokePoints.push(prev);
  }
  return strokePoints;
}
var RATE_OF_CHANGE = 0.3;
function getStrokeOutlinePoints(points, options = {}) {
  const {
    size = 8,
    thinning = 0.5,
    smoothing = 0.5,
    simulatePressure = true,
    easing = (t) => t,
    start = {},
    end = {},
    last: isComplete = false
  } = options;
  let { streamline = 0.5 } = options;
  streamline /= 2;
  const {
    cap: capStart = true,
    taper: taperStart = 0,
    easing: taperStartEase = (t) => t * (2 - t)
  } = start;
  const {
    cap: capEnd = true,
    taper: taperEnd = 0,
    easing: taperEndEase = (t) => --t * t * t + 1
  } = end;
  const len3 = points.length;
  if (len3 === 0)
    return [];
  const totalLength = points[len3 - 1].runningLength;
  const leftPts = [];
  const rightPts = [];
  let prevPressure = points.slice(0, 10).reduce((acc, curr) => {
    let pressure = curr.pressure;
    if (simulatePressure) {
      const sp = min(1, curr.distance / size);
      const rp = min(1, 1 - sp);
      pressure = min(1, acc + (rp - acc) * (sp * RATE_OF_CHANGE));
    }
    return (acc + pressure) / 2;
  }, points[0].pressure);
  let radius = getStrokeRadius(size, thinning, easing, points[len3 - 1].pressure);
  let firstRadius = void 0;
  let prevVector = points[0].vector;
  let pl = points[0].point;
  let pr = pl;
  let tl = pl;
  let tr = pr;
  let short = true;
  for (let i = 0; i < len3 - 1; i++) {
    let { pressure } = points[i];
    const { point, vector, distance, runningLength } = points[i];
    if (i > 0 && short && runningLength < size / 2) {
      continue;
    } else if (short) {
      short = false;
    }
    if (thinning) {
      if (simulatePressure) {
        const sp = min(1, distance / size);
        const rp = min(1, 1 - sp);
        pressure = min(1, prevPressure + (rp - prevPressure) * (sp * RATE_OF_CHANGE));
      }
      radius = getStrokeRadius(size, thinning, easing, pressure);
    } else {
      radius = size / 2;
    }
    if (firstRadius === void 0) {
      firstRadius = radius;
    }
    const ts = runningLength < taperStart ? taperStartEase(runningLength / taperStart) : 1;
    const te = totalLength - runningLength < taperEnd ? taperEndEase((totalLength - runningLength) / taperEnd) : 1;
    radius = Math.max(0.01, radius * Math.min(ts, te));
    const nextVector = points[i + 1].vector;
    const dpr2 = dpr(vector, nextVector);
    if (dpr2 < 0) {
      const offset2 = mul(per(prevVector), radius);
      for (let t = 0; t < 1; t += 0.2) {
        tr = rotAround(add(point, offset2), point, PI * -t);
        tl = rotAround(sub(point, offset2), point, PI * t);
        rightPts.push(tr);
        leftPts.push(tl);
      }
      pl = tl;
      pr = tr;
      continue;
    }
    const offset = mul(per(lrp(nextVector, vector, dpr2)), radius);
    tl = sub(point, offset);
    tr = add(point, offset);
    const alwaysAdd = i < 2 || dpr2 < 0.25;
    const minDistance = Math.pow(Math.max((runningLength > size ? size : size / 2) * smoothing, 1), 2);
    if (alwaysAdd || dist2(pl, tl) > minDistance) {
      leftPts.push(lrp(pl, tl, streamline));
      pl = tl;
    }
    if (alwaysAdd || dist2(pr, tr) > minDistance) {
      rightPts.push(lrp(pr, tr, streamline));
      pr = tr;
    }
    prevPressure = pressure;
    prevVector = vector;
  }
  const firstPoint = points[0];
  const lastPoint = points[len3 - 1];
  const isVeryShort = short || rightPts.length < 2 || leftPts.length < 2;
  if (isVeryShort && (!(taperStart || taperEnd) || isComplete)) {
    let ir = 0;
    for (let i = 0; i < len3; i++) {
      const { pressure, runningLength } = points[i];
      if (runningLength > size) {
        ir = getStrokeRadius(size, thinning, easing, pressure);
        break;
      }
    }
    const start2 = sub(firstPoint.point, mul(per(uni(vec(lastPoint.point, firstPoint.point))), ir || radius));
    const dotPts = [];
    for (let t = 0, step = 0.1; t <= 1; t += step) {
      dotPts.push(rotAround(start2, firstPoint.point, PI * 2 * t));
    }
    return dotPts;
  }
  const startCap = [];
  const endCap = [];
  if (leftPts.length > 1 && rightPts.length > 1) {
    tr = rightPts[1];
    for (let i = 1; i < leftPts.length; i++) {
      if (!isEqual(tr, leftPts[i])) {
        tl = leftPts[i];
        break;
      }
    }
    if (capStart || taperStart) {
      if (!taperStart && !(taperEnd && isVeryShort)) {
        if (!isEqual(tr, tl)) {
          const start2 = sub(firstPoint.point, mul(uni(vec(tr, tl)), dist(tr, tl) / 2));
          for (let t = 0, step = 0.1; t <= 1; t += step) {
            const pt = rotAround(start2, firstPoint.point, PI * t);
            if (dist(pt, tl) < 1)
              break;
            startCap.push(pt);
          }
          leftPts.shift();
          rightPts.shift();
        }
      } else {
        startCap.push(firstPoint.point, add(firstPoint.point, [0.1, 0.1]));
      }
    } else {
      if (!isEqual(tr, tl)) {
        const vector2 = uni(vec(tr, tl));
        const dist3 = dist(tr, tl) / 2;
        startCap.push(sub(firstPoint.point, mul(vector2, dist3 * 0.95)));
        startCap.push(sub(firstPoint.point, mul(vector2, dist3)));
        startCap.push(add(firstPoint.point, mul(vector2, dist3)));
        startCap.push(add(firstPoint.point, mul(vector2, dist3 * 0.95)));
        leftPts.shift();
        rightPts.shift();
      }
    }
    const ll = leftPts[leftPts.length - 1];
    const lr = rightPts[rightPts.length - 1];
    const mid = med(ll, lr);
    const last = lastPoint.point;
    const vector = uni(sub(last, mid));
    if (capEnd || taperEnd) {
      if (!taperEnd && !(taperStart && isVeryShort)) {
        const start2 = add(last, mul(per(vector), radius));
        for (let t = 0, step = 0.1; t <= 1; t += step) {
          const pt = rotAround(start2, last, PI * 3 * t);
          if (dist(pt, lr) < 1)
            break;
          endCap.push(pt);
        }
      } else {
        endCap.push(last);
      }
    } else {
      const justBefore = lrp(mid, last, 0.95);
      const r = radius * 0.95;
      endCap.push(add(justBefore, mul(per(vector), r)));
      endCap.push(add(last, mul(per(vector), r)));
      endCap.push(sub(last, mul(per(vector), r)));
      endCap.push(sub(justBefore, mul(per(vector), r)));
    }
  }
  return leftPts.concat(endCap, rightPts.reverse(), startCap);
}
function getStroke(points, options = {}) {
  return getStrokeOutlinePoints(getStrokePoints(points, options), options);
}
var src_default = getStroke;
export {
  src_default as default,
  getStroke,
  getStrokeOutlinePoints,
  getStrokePoints
};
